/* @flow */
import * as React from 'react';
import { observable, computed, action } from 'mobx';
import { observer } from 'mobx-react';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';

//----------------------------------------------------------------------//
// MATERIAL UI                                                          //
//----------------------------------------------------------------------//

import Chip from '@material-ui/core/Chip';

//----------------------------------------------------------------------//
//----------------------------------------------------------------------//

import PageWrapper from 'components/PageWrapper/PageWrapper';
import FormWrapper from 'components/FormWrapper/FormWrapper';
import FormSection from 'components/FormSection/FormSection';
import ElectroTextField from '../components/inputs/ElectroTextField/ElectroTextField';
import ElectroSelect from '../components/inputs/ElectroSelect/ElectroSelect';
import ElectroRadioGroup from '../components/inputs/ElectroRadioGroup/ElectroRadioGroup';
import ElectroResponseMessage from '../components/inputs/ElectroResponseMessage';
import FormControls from '../components/controls/FormControls/FormControls';

import AutoCompleteData from 'models/forms/AutoCompleteModel.js';
import { autoCompleteManufacturers } from 'api/forms.js';

import ElectroSelectExtra from '../components/inputs/ElectroSelectExtra/ElectroSelectExtra';

import { makeFields, manufacturerCreate, manufacturerEdit } from './setupManufacturerForm';
import ElectroSwitch from '../components/inputs/ElectroSwitch/ElectroSwitch';

type TypeProps = {|
  mode: string,
  itemData: Object,
|} & TranslatorProps;

@translate()
@observer
export default class ManufacturerForm extends React.Component<*> {
  props: TypeProps;

  @observable localState = {
    disableFields: false,
    responseMessage: '',
    showResponseMessage: false,
    isLoading: false,
    hasSuccess: false,
    hasError: false,
    aliases: [],
  };

  @observable model: AutoCompleteData = new AutoCompleteData(autoCompleteManufacturers);

  @computed get form(): Object {
    const itemData: Object = typeof this.props.itemData === 'undefined' ? {} : this.props.itemData;
    const fields: Array<*> = makeFields(itemData, this.props.t);
    return this.props.mode === 'edit'
      ? manufacturerEdit(fields, itemData, this.localState, this.props.t)
      : manufacturerCreate(fields, this.localState, this.props.t);
  }

  componentDidMount() {
    const itemData: Object = typeof this.props.itemData === 'undefined' ? {} : this.props.itemData;
    const aliases = this.props.itemData ? this.props.itemData.aliases : [];
    const isGlobalList: Boolean = itemData.global_list !== undefined ? itemData.global_list : true;

    this.form.$('manufacturerDetail.globalList').set('value', isGlobalList);
    this.localState.aliases = aliases.reduce((keyArr: Array, key: Object) => {
      keyArr.push(key.alias);
      return keyArr;
    }, []);
  }

  updateFields() {
    const itemData: Object = typeof this.props.itemData === 'undefined' ? {} : this.props.itemData;

    // Set as Disabled
    if (itemData.status === 'Pending Review') {
      this.localState.disableFields = true;
    }

    // Update Aliases from chosen Manufacturer
    this.localState.aliases = this.model.chosen.aliases;
  }

  onResetFunc() {
    const itemData: Object = typeof this.props.itemData === 'undefined' ? {} : this.props.itemData;
    const aliases = this.props.itemData ? this.props.itemData.aliases : [];

    // Set as Disabled false
    if (itemData.status === 'Pending Review') {
      this.localState.disableFields = false;
    }

    this.localState.aliases = aliases.reduce((keyArr: Array, key: Object) => {
      keyArr.push(key.alias);
      return keyArr;
    }, []);
  }

  @action handleKeyDown = (e: Object) => {
    const localState = this.localState;
    const alias = this.form.$('manufacturerDetail.manufacturerAlias').value;
    localState.showResponseMessage = false;

    if (e.key === 'Enter') {
      if (alias !== '') {
        const idx: number = localState.aliases.indexOf(alias);

        if (idx > -1) {
          localState.responseMessage = `Alias '${alias}' already exists`;
          localState.showResponseMessage = true;
          localState.hasSuccess = false;
          localState.hasError = true;
        } else {
          localState.aliases.push(alias);
        }
      }

      this.form.$('manufacturerDetail.manufacturerAlias').set('');

      e.preventDefault();
    }
  };

  @action removeAlias(term: string) {
    const idx: number = this.localState.aliases.indexOf(term);

    if (idx !== -1) {
      this.localState.aliases.splice(idx, 1);
    }
  }

  render(): React.Element<*> {
    const itemData: Object = typeof this.props.itemData === 'undefined' ? {} : this.props.itemData;
    const model: Object = this.model;
    const t: Function = this.props.t;

    return (
      <PageWrapper isFlex={false}>
        <ElectroResponseMessage localState={this.localState} />
        <FormWrapper form={this.form}>
          <FormSection form={this.form} section="manufacturerDetail">
            <form>
              {this.props.mode === 'edit' ? (
                <ElectroSelectExtra
                  field={this.form.$('manufacturerDetail.manufacturerName')}
                  form={this.form}
                  formName="manufacturerDetail"
                  search={autoCompleteManufacturers}
                  labelKey="name"
                  extraFilters={['aliases']}
                  updateCustomFields={() => this.updateFields()}
                  onResetFunc={() => this.onResetFunc()}
                  props={{
                    model,
                    itemData,
                  }}
                />
              ) : (
                <ElectroTextField field={this.form.$('manufacturerDetail.manufacturerName')} />
              )}

              <ElectroTextField
                field={this.form.$('manufacturerDetail.manufacturerCode')}
                disabled={this.localState.disableFields}
              />
              <ElectroTextField
                field={this.form.$('manufacturerDetail.manufacturerShortCode')}
                disabled={this.localState.disableFields}
              />
              <ElectroTextField
                field={this.form.$('manufacturerDetail.manufacturerAlias')}
                disabled={this.localState.disableFields}
                onKeyPress={(ev: SyntheticInputEvent<*>): void => this.handleKeyDown(ev)}
              />
              {this.localState.aliases &&
                this.localState.aliases.length > 0 &&
                this.localState.aliases.map(
                  (term: string): React.Element<*> => (
                    <Chip
                      key={term}
                      deleteIcon={
                        <svg>
                          <use xlinkHref="#icon-close" />
                        </svg>
                      }
                      label={term}
                      onDelete={() => this.removeAlias(term)}
                    />
                  )
                )}
              <React.Fragment>
                <ElectroRadioGroup field={this.form.$('manufacturerDetail.keyOEM')} inline={true} />
              </React.Fragment>
              {this.props.mode === 'edit' && (
                <ElectroSelect
                  field={this.form.$('manufacturerDetail.manufacturerStatus')}
                  searchable={false}
                  simpleValue={true}
                  disabled={this.localState.disableFields}
                  labelKey="label"
                  valueKey="id"
                />
              )}
            </form>
            <ElectroSwitch field={this.form.$('manufacturerDetail.globalList')} />
          </FormSection>
          <FormControls
            form={this.form}
            buttonText={
              this.props.mode === 'edit' ? t('general:actions.update') : t('general:actions.create')
            }
          />
        </FormWrapper>
      </PageWrapper>
    );
  }
}
